// src/pages/AboutPrrepX.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import "./AboutPrrepX.css";
import heroImg from "./image/prrepx_kids.png";
import hanumatImg from "./image/hanumat1.png";

/* ─── DATA ──────────────────────────────────────────────────── */

const USP_FACULTY = [
  {
    emoji: "⚛️",
    text: "Our JEE courses: by 100% team of IIT faculty only.",
    tag: "JEE",
  },
  {
    emoji: "🩺",
    text: "Our Medical Course: by 100% team of IIT & top medical alumni only.",
    tag: "NEET",
  },
  {
    emoji: "📚",
    text: "Our Class 6–12th (Board + School) courses: by 100% team of IIT/NIT & top Medical alumni.",
    tag: "Class 6–12",
  },
];

const USP_FEATURES = [
  {
    icon: "🔒",
    title: "Confidential Support",
    body: "Free and public to every student — a safe and private space for everyone.",
    accent: "#e74c3c",
  },
  {
    icon: "🙋",
    title: "Doubt Portal",
    body: "Free and public to every student. First time in India — our doubt support is for everyone.",
    accent: "#8e44ad",
  },
  {
    icon: "📖",
    title: "100% Syllabus — Free",
    body: "Same syllabus you get in any top paid coaching, completely free.",
    accent: "#2980b9",
  },
  {
    icon: "📝",
    title: "500+ Free Mock Tests",
    body: "Chapter-wise and full-chapter tests — same quality as any premium coaching.",
    accent: "#27ae60",
  },
  {
    icon: "🧭",
    title: "Counselling Portal",
    body: "10× more accurate, based on real data & close-to-reality predictions.",
    accent: "#e67e22",
  },
  {
    icon: "🎯",
    title: "One-Stop Solution",
    body: "Recorded (YouTube/Website) + Paid Live Classes + Offline — both Free & Paid, full syllabus. By 100% IIT/NIT & top medical alumni.",
    accent: "#c0392b",
  },
  {
    icon: "✨",
    title: "Many More Features",
    body: "We are constantly building new features to make learning better for every student.",
    accent: "#16a085",
  },
];

const VISION_POINTS = [
  {
    icon: "🎓",
    headline: "100% Faculties from IIT/NIT & Top Medical Alumni",
    sub: [
      "Our JEE courses: 100% team of IIT faculty only.",
      "Our Medical Course: 100% team of IIT & top medical alumni only.",
      "Our Class 6–12th courses: 100% team of IIT/NIT & top Medical alumni.",
      "Now every student will have access to the same quality content as the toppers, regardless of their financial background.",
    ],
  },
  {
    icon: "🌐",
    headline: "One-Stop Solution for JEE / NEET / Class 6–12",
    sub: [
      "Recorded (YouTube & website), Live Classes (online), Offline classes",
      "Free Mock Tests, Premium Test Series, 24*7 Doubt Portal, Confidential Support",
      "Personal Mentoring, Counselling Support, 360° Student Care, Many More.",
      "Whatever a student needs — everything is here, under one roof.",
    ],
  },
  {
    icon: "💚",
    headline: "Free Version + Paid Version — Both Full Syllabus",
    sub: [
      "Even a zero-financial student can prepare fully without any barriers.",
      "12+ Categories of Test Series to complete every aspirants needs.",
      "All free or premium resources are exclusively prepared by 100% team of IIT/NIT & top medical alumni.",
    ],
  },
];

const PRINCIPLES = [
  {
    number: "01",
    keyword: "Mindset",
    title: "Trust your capabilities;",
    subtitle: "never doubt yourself.",
    color: "#c0392b",
    bg: "linear-gradient(135deg,#fbeaea,#fff0f0)",
    border: "#e74c3c",
  },
  {
    number: "02",
    keyword: "Growth",
    title: "Work on weaknesses;",
    subtitle: "never just compete.",
    color: "#8e44ad",
    bg: "linear-gradient(135deg,#f3e8fb,#faf0ff)",
    border: "#9b59b6",
  },
  {
    number: "03",
    keyword: "Process",
    title: "Focus on learning;",
    subtitle: "don't rush for completion.",
    color: "#1a7fc1",
    bg: "linear-gradient(135deg,#e8f4fd,#f0f8ff)",
    border: "#2980b9",
  },
  {
    number: "04",
    keyword: "Action",
    title: "Maximise today;",
    subtitle: "don't leave it for tomorrow.",
    color: "#d35400",
    bg: "linear-gradient(135deg,#fef3e8,#fff8f0)",
    border: "#e67e22",
  },
  {
    number: "05",
    keyword: "Outcome",
    title: "Work hard today;",
    subtitle: "make tomorrow flourish.",
    color: "#1e8449",
    bg: "linear-gradient(135deg,#eafaf1,#f0fff4)",
    border: "#27ae60",
  },
];

/* ─── ANIMATION HOOK ────────────────────────────────────────── */
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("apx-visible");
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ─── SUB-COMPONENTS ────────────────────────────────────────── */

const Section = ({ id, children, delay = 0 }) => {
  const ref = useFadeIn(0.1);
  return (
    <section id={id} ref={ref} className="apx-section apx-fade-in" style={{ "--delay": `${delay}ms` }}>
      {children}
    </section>
  );
};

/* ─── MAIN PAGE ─────────────────────────────────────────────── */
const AboutPrrepX = () => {
  const visionScrollRef = useRef(null);
  const [activeVisionDot, setActiveVisionDot] = useState(0);
  const [visionOverflows, setVisionOverflows] = useState(false);

  // Detect whether the vision grid needs scrolling
  useEffect(() => {
    const el = visionScrollRef.current;
    if (!el) return;
    const check = () => setVisionOverflows(el.scrollWidth > el.clientWidth + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const slideVision = useCallback((idx) => {
    const el = visionScrollRef.current;
    if (!el) return;
    const total = VISION_POINTS.length;
    const clamped = (idx + total) % total;
    setActiveVisionDot(clamped);
    const card = el.children[clamped];
    if (card) {
      el.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    }
  }, []);

  // update dot on manual scroll
  useEffect(() => {
    const el = visionScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardW = (el.children[0]?.offsetWidth || 1) + 28;
      setActiveVisionDot(Math.round(el.scrollLeft / (cardW + 28)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="apx-page">

      {/* ══════════ HERO ══════════ */}
      <div className="apx-hero">
        <div className="apx-hero-glow apx-glow-1" />
        <div className="apx-hero-glow apx-glow-2" />
        <div className="apx-hero-glow apx-glow-3" />

        {/* Left — text */}
        <div className="apx-hero-content">
          <div className="apx-hero-badge">WHO WE ARE</div>
          <h1 className="apx-hero-title">
            <span className="apx-hero-black">About</span>{" "}
            <span className="apx-hero-blue">PrrepX</span>{" "}
            <span className="apx-hero-accent">Pathshala</span>
          </h1>
          <p className="apx-hero-tagline">
            We brought <strong>100% IIT/NIT &amp; top medical alumni</strong> under one roof —
            to ignite every student's potential, no matter where they come from.
          </p>
          <p className="apx-hero-vision">
            Our vision: a future where <strong>no dream is too big</strong> because of money, location, or access —
            where every child from every corner of India can compete, excel, and rise.
          </p>

          <div className="apx-hero-stats">
            <div className="apx-stat"><span className="apx-stat-num">100%</span><span className="apx-stat-label">IIT/NIT/Medical Alumni</span></div>
            <div className="apx-stat-divider" />
            <div className="apx-stat"><span className="apx-stat-num">500+</span><span className="apx-stat-label">Free Mock Tests</span></div>
            <div className="apx-stat-divider" />
            <div className="apx-stat"><span className="apx-stat-num">Free</span><span className="apx-stat-label">Complete Syllabus</span></div>
            <div className="apx-stat-divider" />
            <div className="apx-stat"><span className="apx-stat-num">24/7</span><span className="apx-stat-label">Doubt Support</span></div>
          </div>
        </div>

        {/* Right — image collage */}
        <div className="apx-hero-image-col">
          <div className="apx-hero-img-frame">
            <img src={heroImg} alt="PrrepX Pathshala students" className="apx-hero-main-img" />
            <div className="apx-hero-img-overlay" />
            <div className="apx-hero-img-badge">
              <img src={hanumatImg} alt="Founder" className="apx-founder-thumb" />
              <div>
                <div className="apx-founder-name">Hanumat Lal Vishwakarma</div>
                <div className="apx-founder-role">Founder & Director</div>
              </div>
            </div>
          </div>
          {/* Mobile-only badge — pinned inside image at bottom on small screens */}
          <div className="apx-hero-badge apx-hero-badge-mobile">WHO WE ARE</div>
          {/*
          <div className="apx-hero-float-card apx-float-card-1">🏆 Led by IIT/NIT Alumni</div> */}
          <div className="apx-hero-float-card apx-float-card-2">🚀 JEE · NEET · Class 6–12</div>
        </div>

        <div className="apx-hero-scroll-hint">
          <span>Scroll to explore</span>
          <div className="apx-scroll-arrow" />
        </div>
      </div>

      <div className="apx-body">

        {/* ══════════ USPs ══════════ */}
        <Section id="usps" delay={0}>
          <div className="apx-section-header">
            <div className="apx-section-eyebrow">WHAT MAKES US DIFFERENT</div>
            <h2 className="apx-section-title">PrrepX USPs</h2>
            <p className="apx-section-sub">Why PrrepX Pathshala stands apart from every other coaching platform in India.</p>
          </div>

          {/* Faculty excellence banner */}
          <div className="apx-faculty-banner">
            <div className="apx-faculty-banner-label">FACULTY EXCELLENCE</div>
            <div className="apx-faculty-row">
              {USP_FACULTY.map((f, i) => (
                <div key={i} className="apx-faculty-card">
                  <span className="apx-faculty-emoji">{f.emoji}</span>
                  <div>
                    <span className="apx-faculty-tag">{f.tag}</span>
                    <p className="apx-faculty-text">{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feature USP cards grid */}
          <div className="apx-usp-grid">
            {USP_FEATURES.map((u, i) => (
              <div key={i} className="apx-usp-card" style={{ "--acc": u.accent, animationDelay: `${i * 60}ms` }}>
                <div className="apx-usp-icon">{u.icon}</div>
                <div className="apx-usp-text">
                  <h4 className="apx-usp-title">{u.title}</h4>
                  <p className="apx-usp-body">{u.body}</p>
                </div>
                <div className="apx-usp-bar" />
              </div>
            ))}
          </div>
        </Section>

        {/* ══════════ VISION ══════════ */}
        <Section id="vision" delay={100}>
          <div className="apx-section-header">
            <div className="apx-section-eyebrow">WHERE WE ARE HEADED</div>
            <h2 className="apx-section-title apx-section-title--light">PrrepX Vision</h2>
            <p className="apx-section-sub apx-section-sub--light">Every student has the right to top-quality education — regardless of financial background. That belief drives every decision we make.</p>
          </div>

          <div className="apx-vision-slider-wrap">
            <button className="apx-vision-arrow apx-vision-arrow-left" style={{ visibility: visionOverflows ? 'visible' : 'hidden' }} onClick={() => slideVision(activeVisionDot - 1)} aria-label="Previous">&#8249;</button>
            <div className="apx-vision-container" ref={visionScrollRef}>
              {VISION_POINTS.map((v, i) => (
                <div key={i} className="apx-vision-card" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="apx-vision-num">{String(i + 1).padStart(2, "0")}</div>
                  <div className="apx-vision-icon">{v.icon}</div>
                  <div className="apx-vision-text">
                    <h4 className="apx-vision-headline">{v.headline}</h4>
                    <ul className="apx-vision-sub">
                      {v.sub.map((s, j) => (
                        <li key={j}><span className="apx-vision-bullet" />  {s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            <button className="apx-vision-arrow apx-vision-arrow-right" style={{ visibility: visionOverflows ? 'visible' : 'hidden' }} onClick={() => slideVision(activeVisionDot + 1)} aria-label="Next">&#8250;</button>
          </div>
          {visionOverflows && (
            <div className="apx-vision-dots">
              {VISION_POINTS.map((_, i) => (
                <button key={i} className={`apx-vision-dot${i === activeVisionDot ? ' apx-vision-dot--active' : ''}`} onClick={() => slideVision(i)} aria-label={`Card ${i + 1}`} />
              ))}
            </div>
          )}
        </Section>

        {/* ══════════ PRINCIPLES ══════════ */}
        <Section id="principles" delay={200}>
          <div className="apx-section-header">
            <div className="apx-section-eyebrow">HOW WE THINK & WORK</div>
            <h2 className="apx-section-title">PrrepX Five Principles of Success</h2>
            <p className="apx-section-sub">These five principles are the foundation of everything we build and everything we teach.</p>
          </div>

          <div className="apx-principles-grid">
            {PRINCIPLES.map((p, i) => (
              <div
                key={i}
                className="apx-principle-card"
                style={{ background: p.bg, "--p-color": p.color, "--p-border": p.border, animationDelay: `${i * 80}ms` }}
              >
                <div className="apx-principle-num" style={{ color: p.color }}>{p.number}</div>
                <div className="apx-principle-keyword" style={{ color: p.color, borderColor: p.border }}>{p.keyword}</div>
                <div className="apx-principle-body">
                  <span className="apx-principle-title">{p.title}</span>
                  <span className="apx-principle-sub">{p.subtitle}</span>
                </div>
                <div className="apx-principle-accent-bar" style={{ background: p.color }} />
              </div>
            ))}
          </div>
        </Section>

        {/* ══════════ CLOSING QUOTE ══════════ */}
        <div className="apx-closing">
          <div className="apx-closing-inner">
            <div className="apx-closing-quote">"</div>
            <p className="apx-closing-text">
              We are not just building a coaching platform.<br />
              We are building <span className="apx-closing-accent">India's most equitable educational ecosystem</span> —<br />
              where every child, rich or poor, gets their fair shot at their dream.
            </p>
            <div className="apx-closing-sig">— PrrepX Pathshala Team</div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutPrrepX;
